import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'
import { getChatCompletion } from './openaiApiHandler'

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => {
    const result = reader.result
    resolve(typeof result === 'string' ? result.split(',')[1] : '')
  }
  reader.onerror = () => reject(reader.error || new Error('Unable to read image'))
  reader.readAsDataURL(blob)
})

export const processScreenshotWithMathpix = async (imageBlob) => {
  try {
    // OCR runs in a Cloud Function so the Mathpix credentials stay server-side
    const imageBase64 = await blobToBase64(imageBlob)
    const processMathpixOCR = httpsCallable(functions, 'processMathpixOCR')
    const ocrResult = await processMathpixOCR({ imageBase64 })
    const rawText = ocrResult.data?.text || ocrResult.data?.latex || 'Image question'

    // Process with OpenAI to format questions
    const messages = [
      {
        role: "system",
        content: `Question formatter. Rules:
- If content has multiple parts (a, b, c, 1, 2, 3, etc.), create separate questions for each part
- If there's one set of instructions for all parts, include those instructions in each question
- Remove prefixes like "Example 1", "a.", "B)", "2)", "Question 3:", etc. from questions
- Keep question wording and expressions EXACTLY as written - do not modify or rephrase
- Use $ delimiters for inline math (e.g., $x^2$) instead of LaTeX \\( \\) delimiters
- Return VALID JSON only: [{"question":"formatted text"}]`
      },
      {
        role: "user",
        content: `Format this content: ${rawText}

Output JSON array with properly formatted questions. Use $ for math delimiters, not \\( \\).`
      }
    ]

    const openaiResponse = await getChatCompletion(messages, 'gpt-4')

    let formattedQuestions
    try {
      // Remove markdown code blocks
      let cleanedResponse = openaiResponse.replace(/```json\n?|\n?```/g, '').trim()

      // Escape any bare backslashes that are not valid JSON escape sequences
      // (e.g. LaTeX \frac, \sin, \times, etc. need to become \\frac, \\sin, etc.)
      cleanedResponse = cleanedResponse.replace(/\\(?!["\\/bfnrtu0-9])/g, '\\\\')

      // Replace LaTeX delimiters \\( and \\) with $ delimiters (after backslash fix)
      cleanedResponse = cleanedResponse.replace(/\\\\\(/g, '$').replace(/\\\\\)/g, '$')

      // Try to parse the response
      formattedQuestions = JSON.parse(cleanedResponse)

      // Ensure it's an array
      if (!Array.isArray(formattedQuestions)) {
        formattedQuestions = [formattedQuestions]
      }
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', parseError)

      // Fallback: use raw text as single question
      formattedQuestions = [{ question: rawText }]
    }

    return {
      formattedQuestions,
      rawText
    }
  } catch (error) {
    console.error('Error processing screenshot:', error)
    throw error
  }
}
